import { ReactNode, ButtonHTMLAttributes, AnchorHTMLAttributes } from 'react';
import styles from './Button.module.css';

type ButtonVariant = 'primary' | 'secondary' | 'link' | 'ghost';
type IconPosition = 'before' | 'after';

interface BaseProps {
  children: ReactNode;
  variant?: ButtonVariant;
  icon?: ReactNode;
  iconPosition?: IconPosition;
  className?: string;
}

type ButtonAsButtonProps = BaseProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: never;
  };

type ButtonAsLinkProps = BaseProps &
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    href: string;
  };

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

export default function Button({
  children,
  variant = 'primary',
  icon,
  iconPosition = 'before',
  className,
  ...props
}: ButtonProps) {
  const variantClass = styles[variant];
  const combinedClassName = `${styles.button} ${variantClass} ${className || ''}`.trim();
  const textClass = (styles as Record<string, string>)[`${variant}Text`] ?? '';

  // Render content based on variant
  const renderContent = () => {
    if (variant === 'ghost') {
      return children;
    }

    if (variant === 'link') {
      return (
        <>
          {icon && iconPosition === 'before' && (
            <span className={styles.buttonIcon}>{icon}</span>
          )}
          <span className={`${styles.buttonText} ${textClass}`.trim()}>{children}</span>
          {icon && iconPosition === 'after' && (
            <span className={styles.buttonIcon}>{icon}</span>
          )}
        </>
      );
    }

    return <span className={`${styles.buttonText} ${textClass}`.trim()}>{children}</span>;
  };

  if ('href' in props && props.href) {
    const { href, ...anchorProps } = props;
    return (
      <a className={combinedClassName} href={href} {...anchorProps}>
        {renderContent()}
      </a>
    );
  }

  const { type = 'button', ...buttonProps } = props as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button className={combinedClassName} type={type} {...buttonProps}>
      {renderContent()}
    </button>
  );
}
